import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { StoreModel } from '../..';
import type { ApplicationErrorOptions } from '../../../types';
import type {
  CarrierAccountCreate,
  CarrierAccount,
  CarrierAccountUpdate,
  FetchAssetParams,
  UpdateCarrierAccountTagging,
  CarrierAccountSearchParams,
  CarrierAccountSearchResponse,
} from '../types';
import parseStatusCode from '../../errors/parseStatusCode';

export interface CarrierAccountModel {
  createCarrierAccount: Thunk<
    CarrierAccountModel,
    CarrierAccountCreate,
    Injections,
    StoreModel,
    Promise<CarrierAccount>
  >;
  fetchCarrierAccounts: Thunk<CarrierAccountModel, FetchAssetParams, Injections, Promise<CarrierAccount[]>>;
  searchCarrierAccounts: Thunk<
    CarrierAccountModel,
    CarrierAccountSearchParams,
    Injections,
    Promise<CarrierAccountSearchResponse[]>
  >;
  getOriginCarrierAccountMapping: Thunk<
    CarrierAccountModel,
    string[],
    Injections,
    Promise<Record<string, CarrierAccountSearchResponse[]>>
  >;
  updateCarrierAccount: Thunk<
    CarrierAccountModel,
    CarrierAccountUpdate,
    Injections,
    StoreModel,
    Promise<CarrierAccount>
  >;
  updateCarrierAccountTagging: Thunk<
    CarrierAccountModel,
    UpdateCarrierAccountTagging,
    Injections,
    StoreModel,
    Promise<CarrierAccount>
  >;
  deleteCarrierAccountTagging: Thunk<
    CarrierAccountModel,
    UpdateCarrierAccountTagging,
    Injections,
    StoreModel,
    Promise<CarrierAccount>
  >;
  carrierAccountActionStart: Action<CarrierAccountModel, void>;
  lastAction?: number;
  carrierAccounts: Record<string, CarrierAccount>;
  carrierAccountsWithOrigin: CarrierAccountSearchResponse[];
  originCarrierAccountMap: Record<string, CarrierAccountSearchResponse[]>;
  addCarrierAccount: Action<CarrierAccountModel, CarrierAccount>;
  setCarrierAccounts: Action<CarrierAccountModel, CarrierAccount[]>;
  setCarrierAccountsWithOrigin: Action<CarrierAccountModel, CarrierAccountSearchResponse[]>;
  setOriginCarrierAccountMap: Action<CarrierAccountModel, Record<string, CarrierAccountSearchResponse[]>>;
  carrierAccountFailure: Action<CarrierAccountModel, ApplicationErrorOptions>;
}

export const carrierAccountModel: CarrierAccountModel = {
  carrierAccounts: {},
  carrierAccountsWithOrigin: [],
  originCarrierAccountMap: {},
  carrierAccountFailure: action((state, payload) => {
    console.error(payload);
  }),
  // add or update single carrier account in cache
  addCarrierAccount: action((state, payload) => {
    state.carrierAccounts = { ...state.carrierAccounts, [payload.carrierAccountId]: payload };
  }),
  // assert a list of carrierAccounts represents what is in cached
  setCarrierAccounts: action((state, payload) => {
    const map: Record<string, CarrierAccount> = {};
    for (const val of payload) {
      map[val.carrierAccountId] = val;
    }
    state.carrierAccounts = map;
  }),
  setOriginCarrierAccountMap: action((state, payload) => {
    state.originCarrierAccountMap = payload;
  }),
  setCarrierAccountsWithOrigin: action((state, payload) => {
    state.carrierAccountsWithOrigin = payload;
  }),
  carrierAccountActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  createCarrierAccount: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const res = await injections.assetService.createCarrierAccount(payload);
      actions.addCarrierAccount(res);

      // Invalidate carrier account queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-create',
        entityId: res.carrierAccountId,
      });

      return res;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating carrier account.',
        asNotification: true,
      });
      throw error;
    }
  }),
  fetchCarrierAccounts: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.assetService.fetchCarrierAccounts(payload);
      actions.setCarrierAccounts(res);
      return res;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching carrier accounts.',
        asNotification: true,
      });
    }
  }),
  searchCarrierAccounts: thunk(async (actions, payload, { injections }) => {
    const { assetService } = injections;
    try {
      const response = await assetService.searchCarrierAccounts(payload);
      actions.setCarrierAccountsWithOrigin(response);
      return response;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured searching carrier accounts.',
        asNotification: true,
      });
    }
  }),
  getOriginCarrierAccountMapping: thunk(async (actions, originIds, { injections }) => {
    const { assetService } = injections;
    try {
      const promises = originIds.map((originId) =>
        assetService.searchCarrierAccounts({ originId }).then((response) => ({ originId, response })),
      );

      const results = await Promise.all(promises);

      const originCarrierAccountMap: Record<string, CarrierAccountSearchResponse[]> = {};
      results.forEach(({ originId, response }) => {
        originCarrierAccountMap[originId] = response;
      });
      actions.setOriginCarrierAccountMap(originCarrierAccountMap);
      return originCarrierAccountMap;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured searching carrier accounts.',
        asNotification: true,
      });
    }
  }),
  updateCarrierAccount: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const res = await injections.assetService.updateCarrierAccount(payload);
      actions.addCarrierAccount(res);

      // Invalidate carrier account queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-update',
        entityId: payload.carrierAccountId,
      });

      return res;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating carrier account',
        asNotification: true,
      });
      throw error;
    }
  }),
  updateCarrierAccountTagging: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const res = await injections.assetService.updateCarrierAccountTagging(payload.carrierAccountId, payload.tagId);
      actions.addCarrierAccount(res);

      // Invalidate carrier account queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-link',
        entityId: payload.carrierAccountId,
      });

      return res;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating carrier account tagging',
        asNotification: true,
      });
      return error;
    }
  }),
  deleteCarrierAccountTagging: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      const res = await injections.assetService.deleteCarrierAccountTagging(payload.carrierAccountId, payload.tagId);
      actions.addCarrierAccount(res);

      // Invalidate carrier account queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'carrier-account-delete',
        entityId: payload.carrierAccountId,
      });

      return res;
    } catch (error) {
      actions.carrierAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured deleting carrier account tag',
        asNotification: true,
      });
      return error;
    }
  }),
};
